/**
 * NotificationItem Component
 * แสดง notification แต่ละรายการ พร้อม icon, title, message, relative_time
 */

import React from 'react'
import { motion } from 'framer-motion'
import {
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserPlusIcon,
  BellIcon,
  EnvelopeIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline'
import { Notification, NotificationEventType, getNotificationColor } from '../../types/notification'

interface NotificationItemProps {
  notification: Notification
  onClick?: (notification: Notification) => void
  className?: string
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onClick, className = '' }) => {
  const { is_read, type, title, message, relative_time } = notification

  // เลือก icon ตาม notification type
  const getIcon = () => {
    switch (type) {
      // Pending notifications (Yellow)
      case NotificationEventType.COMPANY_PENDING:
      case NotificationEventType.UPDATE_PENDING:
      case NotificationEventType.CLAIM_PENDING:
        return <ClockIcon className="w-5 h-5" />

      // Approved notifications (Green)
      case NotificationEventType.COMPANY_APPROVED:
      case NotificationEventType.UPDATE_APPROVED:
      case NotificationEventType.CLAIM_APPROVED:
        return <CheckCircleIcon className="w-5 h-5" />

      // Rejected notifications (Red)
      case NotificationEventType.COMPANY_REJECTED:
      case NotificationEventType.UPDATE_REJECTED:
      case NotificationEventType.CLAIM_REJECTED:
        return <XCircleIcon className="w-5 h-5" />

      // Claim submitted (Blue)
      case NotificationEventType.CLAIM_SUBMITTED:
        return <DocumentTextIcon className="w-5 h-5" />

      // Contact form (Blue)
      case NotificationEventType.CONTACT_FORM:
        return <EnvelopeIcon className="w-5 h-5" />

      // New member (Blue)
      case NotificationEventType.NEW_MEMBER:
        return <UserPlusIcon className="w-5 h-5" />

      // System & others (Gray)
      case NotificationEventType.SYSTEM:
      default:
        return <BellIcon className="w-5 h-5" />
    }
  }

  // เลือกสีตาม notification type
  const getColorClasses = () => {
    const color = getNotificationColor(type)
    const colorMap: Record<string, { bg: string; text: string; border: string }> = {
      green: {
        bg: 'bg-green-50',
        text: 'text-green-600',
        border: 'border-green-200',
      },
      red: {
        bg: 'bg-red-50',
        text: 'text-red-600',
        border: 'border-red-200',
      },
      yellow: {
        bg: 'bg-yellow-50',
        text: 'text-yellow-600',
        border: 'border-yellow-200',
      },
      blue: {
        bg: 'bg-blue-50',
        text: 'text-blue-600',
        border: 'border-blue-200',
      },
      gray: {
        bg: 'bg-gray-50',
        text: 'text-gray-600',
        border: 'border-gray-200',
      },
    }
    return colorMap[color] || colorMap.gray
  }

  const colorClasses = getColorClasses()

  const handleClick = () => {
    if (onClick) {
      onClick(notification)
    }
  }

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      onClick={handleClick}
      className={`
        flex items-start space-x-3 p-4 rounded-lg transition-all duration-200 cursor-pointer
        ${is_read ? 'bg-white hover:bg-gray-50' : 'bg-blue-50/50 hover:bg-blue-50'}
        border ${is_read ? 'border-gray-100' : 'border-blue-200'}
        ${className}
      `}
    >
      {/* Icon Container */}
      <div className={`flex-shrink-0 w-10 h-10 rounded-full ${colorClasses.bg} flex items-center justify-center ${colorClasses.text}`}>
        {getIcon()}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          {/* Title */}
          <h4 className={`text-sm font-semibold ${is_read ? 'text-gray-900' : 'text-gray-900'}`}>
            {title}
          </h4>

          {/* Unread Badge */}
          {!is_read && (
            <div className="flex-shrink-0">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            </div>
          )}
        </div>

        {/* Message */}
        <p className={`text-sm mt-1 ${is_read ? 'text-gray-600' : 'text-gray-700'} line-clamp-2`}>
          {message}
        </p>

        {/* Relative Time */}
        <p className="text-xs text-gray-500 mt-2">
          {relative_time}
        </p>
      </div>
    </motion.div>
  )
}

export default NotificationItem
